/**
 * Auto-Captions — Multi-Format Export
 * ──────────────────────────────────────────────────────────────────────────
 * One transcribe, many platform-tuned exports. OpusClip charges for this;
 * Timbrica gives it away because the captions/burn pipeline is already
 * client-side and re-running the FFmpeg pass per-format is "free" wallclock
 * once the model and ASS payload are in memory.
 *
 * Five platform presets the marketing team agreed on. All five share
 * fps=30 (mobile-first, matches the default capture/edit rate on TikTok,
 * Reels, Shorts and most phone cameras). Aspect ratios:
 *   - tiktok / reels / shorts  → 9:16 vertical (1080×1920)
 *   - fb_4_5                   → 4:5 portrait  (1080×1350)
 *   - yt_16_9                  → 16:9 widescreen (1920×1080)
 *
 * UI contract:
 *   The blade adds a #ac-multi-format card with checkbox chips, one per
 *   preset. When >1 chip is checked we hide #ac-export-burn and reveal
 *   #ac-multi-burn-all. When ≤1 chip is checked we restore the single
 *   burn button. Multi-export sequentially re-runs the burn pipeline for
 *   each selected format (transcript and ASS are cached in state — only
 *   the FFmpeg pass repeats with a scale+pad filter prepended).
 *
 * Why not a single multi-output FFmpeg invocation:
 *   Each preset wants a different scale/pad filter, and emoji overlay
 *   uses filter_complex which is hostile to multi-output. Sequential is
 *   simpler, gives per-format progress, and lets the user cancel mid-batch.
 *
 * Telemetry: emits multi_format_started, multi_format_progress,
 * multi_format_completed via window.__ae.
 */
(function(){
'use strict';

/* ═══════════════════════════════════════════
   Preset table — single source of truth
   ═══════════════════════════════════════════ */

var PRESETS = {
    tiktok:  { id: 'tiktok',  name: 'TikTok',          w: 1080, h: 1920, fps: 30, label: '9:16' },
    reels:   { id: 'reels',   name: 'Reels',           w: 1080, h: 1920, fps: 30, label: '9:16' },
    shorts:  { id: 'shorts',  name: 'YouTube Shorts',  w: 1080, h: 1920, fps: 30, label: '9:16' },
    fb_4_5:  { id: 'fb_4_5',  name: 'Facebook 4:5',    w: 1080, h: 1350, fps: 30, label: '4:5'  },
    yt_16_9: { id: 'yt_16_9', name: 'YouTube 16:9',    w: 1920, h: 1080, fps: 30, label: '16:9' },
};

var PRESET_ORDER = ['tiktok', 'reels', 'shorts', 'fb_4_5', 'yt_16_9'];

/* ═══════════════════════════════════════════
   DOM helpers (light-weight; no jQuery)
   ═══════════════════════════════════════════ */

function $(id){ return document.getElementById(id); }
function getCheckboxes(){
    /* All multi-format chips live under #ac-multi-format-chips; each has
       data-mf-format="<presetId>" and an input[type=checkbox]. */
    var root = $('ac-multi-format-chips');
    if (!root) return [];
    return Array.prototype.slice.call(root.querySelectorAll('input[type=checkbox][data-mf-format]'));
}

/* ═══════════════════════════════════════════
   Public API
   ═══════════════════════════════════════════ */

function getSelectedFormats(){
    var out = [];
    var boxes = getCheckboxes();
    for (var i = 0; i < boxes.length; i++){
        if (!boxes[i].checked) continue;
        var id = boxes[i].getAttribute('data-mf-format');
        var p = PRESETS[id];
        if (p) out.push({ id: p.id, name: p.name, w: p.w, h: p.h, fps: p.fps, label: p.label });
    }
    return out;
}

/**
 * Build the filter + output overrides for a single format. The integrator
 * merges these into exporter.plan({ ... }) — we return a plain object so
 * the existing burn pipeline can spread it into its own plan call.
 *
 * The scale filter uses `force_original_aspect_ratio=decrease,pad=...` so
 * source content is letterboxed (never cropped/stretched) and the padded
 * area is opaque black — standard for cross-platform reposting.
 *
 *   extraFilters    — prepended to whatever filters the burn path already
 *                     applies (resize → reframe → zoom → libass). The
 *                     integrator decides ordering; we just return the
 *                     scale+pad string.
 *   outputBase      — file basename hint (no extension). The burn path
 *                     appends `-captioned` already; we suffix the platform
 *                     id so multi-export saves don't clobber each other.
 *   audioCopy       — true. Caption burn doesn't touch audio.
 *
 * Note: we deliberately DO NOT set `-r` here; the exporter will pass through
 * FPS via its own plan(). If the caller wants exact 30fps output, they pass
 * it on the exporter (videoExporter.setFps(format.fps)) before plan().
 */
function planForFormat(format){
    if (!format || typeof format.w !== 'number' || typeof format.h !== 'number') {
        throw new Error('planForFormat: invalid format');
    }
    var w = format.w, h = format.h;
    /* scale=...:force_original_aspect_ratio=decrease  → keep aspect, fit in box
       pad=...:(ow-iw)/2:(oh-ih)/2                     → centre with black bars
       setsar=1                                        → defensive against odd-sar inputs */
    var scaleFilter =
        'scale=' + w + ':' + h + ':force_original_aspect_ratio=decrease,' +
        'pad=' + w + ':' + h + ':(ow-iw)/2:(oh-ih)/2:black,setsar=1';

    return {
        extraFilters: scaleFilter,
        outputBase:   '-' + format.id,    // suffix appended by integrator (basename + "-captioned" + this)
        audioCopy:    true,
        /* Convenience metadata so the integrator can stamp telemetry / UI. */
        width:  w,
        height: h,
        fps:    format.fps,
        formatId: format.id,
    };
}

function shouldUseMultiFormat(){
    return getSelectedFormats().length > 1;
}

/**
 * Sequentially invoke `callback(format, index, total)` for every checked
 * preset. Awaits each invocation; aborts the loop if the callback throws.
 * The caller is responsible for showing per-format progress / toasts.
 *
 * Returns a summary { done: n, total: m, errors: [...] }.
 */
async function forEachFormat(callback){
    var formats = getSelectedFormats();
    var summary = { done: 0, total: formats.length, errors: [] };
    if (!formats.length) return summary;

    try { if (window.__ae) window.__ae('multi_format_started', { count: formats.length, ids: formats.map(function(f){ return f.id; }).join(',') }); } catch(_){}

    for (var i = 0; i < formats.length; i++){
        var f = formats[i];
        try {
            try { if (window.__ae) window.__ae('multi_format_progress', { id: f.id, index: i, total: formats.length }); } catch(_){}
            // eslint-disable-next-line no-await-in-loop -- sequential is the contract
            await callback(f, i, formats.length);
            summary.done++;
        } catch (err){
            summary.errors.push({ id: f.id, message: (err && err.message) || String(err) });
            /* Hard-fail on first error so we don't silently dribble out
               half-written files. The caller can re-trigger after fixing
               whatever (e.g. canceled FFmpeg session). */
            break;
        }
    }

    try {
        if (window.__ae) window.__ae('multi_format_completed', {
            done:   summary.done,
            total:  summary.total,
            errors: summary.errors.length,
        });
    } catch(_){}

    return summary;
}

/* ═══════════════════════════════════════════
   UI wiring — chip toggle ↔ burn-button swap
   ═══════════════════════════════════════════ */

/**
 * Wire the checkbox change handler so #ac-export-burn and
 * #ac-multi-burn-all swap visibility based on selection count.
 * Safe to call multiple times (idempotent — guards on data-mf-wired).
 */
function wireUI(){
    var root = $('ac-multi-format-chips');
    if (!root || root.getAttribute('data-mf-wired') === '1') return;
    root.setAttribute('data-mf-wired', '1');

    var boxes = getCheckboxes();
    boxes.forEach(function(box){
        box.addEventListener('change', refreshButtonVisibility);
    });
    refreshButtonVisibility();
}

function refreshButtonVisibility(){
    var single = $('ac-export-burn');
    var multi  = $('ac-multi-burn-all');
    var multiCard = $('ac-multi-format');
    if (!single || !multi) return;

    var sel = getSelectedFormats();
    var useMulti = sel.length > 1;

    if (useMulti) {
        single.style.display = 'none';
        multi.style.display = '';
        multi.disabled = single.disabled;          // mirror the gate (captions ready / not burning)
    } else {
        single.style.display = '';
        multi.style.display = 'none';
    }

    /* Update the count placeholder in the multi-button label.
       The blade renders ":count formats" — we replace at runtime so users
       see "Burn 3 formats" not "Burn :count formats". */
    var lbl = multi.querySelector('[data-mf-label-count]');
    if (lbl) {
        var tpl = lbl.getAttribute('data-mf-label-tpl') || lbl.textContent || '';
        lbl.textContent = tpl.replace(':count', String(sel.length));
    }

    /* Card surfaces — only visible once at least one preset is selectable
       (i.e. captions exist). The integrator toggles the card hidden via
       data-mf-ready on the document body when transcripts are ready. */
    if (multiCard) {
        var ready = document.body && document.body.getAttribute('data-mf-ready') === '1';
        multiCard.hidden = !ready;
    }
}

/* Auto-wire on DOM ready so the blade doesn't need any glue code beyond
   the markup. Repeats on tool-ready event (multi-stage hydration). */
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireUI);
} else {
    wireUI();
}
window.addEventListener('ac-captions-ready', function(){
    /* The pipeline raises this event when state.words is populated.
       Mark body so the card un-hides, then refresh. */
    if (document.body) document.body.setAttribute('data-mf-ready', '1');
    wireUI();
    refreshButtonVisibility();
});

/* ═══════════════════════════════════════════
   Export surface
   ═══════════════════════════════════════════ */

window.__acMultiFormat = {
    PRESETS:               PRESETS,
    PRESET_ORDER:          PRESET_ORDER,
    getSelectedFormats:    getSelectedFormats,
    planForFormat:         planForFormat,
    shouldUseMultiFormat:  shouldUseMultiFormat,
    forEachFormat:         forEachFormat,
    /* Test seam — Playwright stubs DOM then calls refreshButtonVisibility()
       directly to verify swap logic without hitting FFmpeg. */
    _refresh:              refreshButtonVisibility,
    _wireUI:               wireUI,
};

})();
